/**
 * @file ConsentLeaveGuard.tsx
 * @description «Правки не сохранены» при уходе с формы новой редакции
 * (phase19.16, Р3г; uiux-r3g-consent-texts §5; решение §8 п. 5 — черновик
 * живёт в браузере): [Сохранить черновик] [Уйти без сохранения]. Переход
 * внутри кабинета, в том числе смена вида (`?kind=`), — окно кабинета
 * (`useBlocker`); закрытие вкладки — окно браузера (`beforeunload`).
 */

import { useEffect } from "react";
import { useBlocker } from "react-router";

import { Button } from "../../ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";

interface Props {
  when: boolean;
  /** Сохранить черновик; true — сохранён, можно уходить. */
  onSave: () => boolean;
  /** Переход уже разрешён (после публикации). */
  skip?: () => boolean;
}

export function ConsentLeaveGuard({ when, onSave, skip }: Props) {
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!when || skip?.()) return false;
    return currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search;
  });

  useEffect(() => {
    if (!when) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [when]);

  const open = blocker.state === "blocked";
  return (
    <AlertDialog open={open} onOpenChange={(next) => (!next && open ? blocker.reset?.() : null)}>
      <AlertDialogContent fullScreen className="max-w-md gap-3">
        <AlertDialogHeader>
          <AlertDialogTitle>Правки не сохранены</AlertDialogTitle>
          <AlertDialogDescription>Черновик хранится только в этом браузере.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="ghost" className="min-h-12" onClick={() => blocker.proceed?.()}>
            Уйти без сохранения
          </Button>
          <Button
            className="min-h-12"
            onClick={() => {
              if (onSave()) blocker.proceed?.();
              else blocker.reset?.();
            }}
          >
            Сохранить черновик
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
